import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getPointsByBatch } from "../../API/points";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TableSortLabel,
  TextField,
} from "@mui/material";

interface ViewAllocatedCardsProps {
  open: boolean;
  onClose: () => void;
  batchId: string;
}

type CardAllocation = {
  _id: string;
  user: { _id: string; name: string };
  card: { _id: string; name: string; value: number; logo: string };
  allocatedAt: string;
};

const ViewAllocatedCards: React.FC<ViewAllocatedCardsProps> = ({ open, onClose, batchId }) => {
  const [loading, setLoading] = useState(true);
  const [batchCards, setBatchCards] = useState<CardAllocation[]>([]);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (open) {
      fetchBatchCards();
    }
  }, [open]);

  const fetchBatchCards = async () => {
    setLoading(true);
    try {
      const response = await getPointsByBatch(batchId);
      setBatchCards(response?.data || []);
    } catch (error) {
      toast.error("Error fetching batch cards");
    }
    setLoading(false);
  };

  // 🔹 Apply Search
  const filteredCards = batchCards.filter(
    (allocation) =>
      allocation.user?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      allocation.card?.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // 🔹 Apply Sorting (Allocated Date)
  const sortedCards = [...filteredCards].sort((a, b) => {
    const aValue = new Date(a.allocatedAt).getTime();
    const bValue = new Date(b.allocatedAt).getTime();
    return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Allocated Cards for Batch</DialogTitle>
      <DialogContent>
        {/* 🔎 Search Input */}
        <TextField
          fullWidth
          label="Search by Student or Card Name"
          variant="outlined"
          margin="dense"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        {loading ? (
          <CircularProgress />
        ) : sortedCards.length > 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student Name</TableCell>
                  <TableCell>Card Name</TableCell>
                  <TableCell>Card Value</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active
                      direction={sortOrder}
                      onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                    >
                      Allocated Date
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedCards.map((allocation) => (
                  <TableRow key={allocation._id}>
                    <TableCell>{allocation.user?.name || "N/A"}</TableCell>
                    <TableCell>
                      {allocation.card ? (
                        <>
                          <img
                            src={allocation.card.logo}
                            alt={allocation.card.name}
                            width={30}
                            height={30}
                            style={{ marginRight: 8 }}
                          />
                          {allocation.card.name}
                        </>
                      ) : (
                        "No Card"
                      )}
                    </TableCell>
                    <TableCell>{allocation.card?.value ?? "N/A"}</TableCell>
                    <TableCell>
                      {allocation.allocatedAt
                        ? new Date(allocation.allocatedAt).toLocaleDateString()
                        : "N/A"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <p>No cards allocated for this batch.</p>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewAllocatedCards;
